import React, { createContext, useCallback, useEffect, useState } from 'react';
import { UnleashClient } from 'unleash-proxy-client';
import envVariables from '../../env';

export type FeatureFlagState = {
  flags: {
    'mycambridge.users-page'?: boolean;
    'mycambridge.subjects'?: boolean;
    'mycambridge.profile-photo'?: boolean;
    'mycambridge.resend-invitation'?: boolean;
  };
  haveFlagsLoaded: boolean;
  isEnabled: (flagName: string) => boolean;
  updateContext: (context: {
    userId?: string;
    properties?: Record<string, any>;
  }) => void;
};

export const FeatureFlagContext = createContext<FeatureFlagState>({
  flags: {},
  haveFlagsLoaded: false,
  isEnabled: () => false,
  updateContext: () => {
    // Default no-op function for context initialization
  },
});

const unleash = new UnleashClient({
  url: envVariables.FEATURE_FLAG_PROXY_URL,
  clientKey: envVariables.FEATURE_FLAG_CLIENT_KEY,
  refreshInterval: envVariables.FEATURE_FLAG_REFRESH_INTERVAL,
  appName: envVariables.FEATURE_FLAG_APP_NAME,
  context: { properties: { environmentName: envVariables.NODE_ENV } },
});

export const FeatureFlagProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState({
    'mycambridge.users-page': unleash.isEnabled('mycambridge.users-page'),
    'mycambridge.subjects': unleash.isEnabled('mycambridge.subjects'),
    'mycambridge.profile-photo': unleash.isEnabled('mycambridge.profile-photo'),
    'mycambridge.resend-invitation': unleash.isEnabled(
      'mycambridge.resend-invitation'
    ),
  });
  const [haveFlagsLoaded, setHaveFlagsLoaded] = useState(false);

  useEffect(() => {
    unleash.start();

    unleash.on('ready', () => {
      setHaveFlagsLoaded(true);
    });

    unleash.on('update', () => {
      setFlags({
        'mycambridge.users-page': unleash.isEnabled('mycambridge.users-page'),
        'mycambridge.subjects': unleash.isEnabled('mycambridge.subjects'),
        'mycambridge.profile-photo': unleash.isEnabled(
          'mycambridge.profile-photo'
        ),
        'mycambridge.resend-invitation': unleash.isEnabled(
          'mycambridge.resend-invitation'
        ),
      });
    });

    return () => {
      unleash.stop();
    };
  }, []);

  const updateContext = (context: {
    userId?: string;
    properties?: Record<string, any>;
  }) => {
    unleash.updateContext(context);
  };

  const isEnabled = useCallback(
    (flagName: string) => {
      return unleash.isEnabled(flagName);
    },
    [flags]
  );

  return (
    <FeatureFlagContext.Provider
      value={{
        flags,
        haveFlagsLoaded,
        isEnabled,
        updateContext,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};
